/**
 * Self-hosted DocuSeal connection config for the in-house signer.
 *
 * Read with fallbacks (NOT validateEnvVariable, which throws): this module is
 * pulled into the app-wide `environment` aggregator, so a required/throwing var
 * would crash boot in every environment until the signer ships.
 */

const DOCUSEAL_BASE_URL = import.meta.env.VITE_DOCUSEAL_BASE_URL ?? '';
const DOCUSEAL_SELF_HOSTED =
  import.meta.env.VITE_DOCUSEAL_SELF_HOSTED === 'true';

// Bare hostname of the base URL (e.g. "docuseal.maxhome.ai") for the @docuseal/react
// `host` prop. '' when unset/invalid → callers fall back to the SaaS default.
const DOCUSEAL_HOST = (() => {
  if (!DOCUSEAL_BASE_URL) return '';
  try {
    return new URL(DOCUSEAL_BASE_URL).host;
  } catch {
    return '';
  }
})();

/**
 * Hostname for `@docuseal/react`'s `host` prop — the self-hosted host whenever a base
 * URL is configured, else `undefined` (the SaaS default). Keyed to infra (the env var),
 * NOT the INHOUSE_SIGNING flag: which DocuSeal instance the builder/embed talks to is
 * about where templates + tokens live, independent of whether the in-house signer is live.
 */
export const getDocusealHost = (): string | undefined =>
  DOCUSEAL_HOST || undefined;

/**
 * True when a self-hosted DocuSeal instance is configured (a parseable base URL). The
 * in-house signer requires this — its `/embed/*` transport hard-fails without a base URL —
 * so the route guard gates the in-house UI on this AND the INHOUSE_SIGNING flag.
 *
 * Note: distinct from `DOCUSEAL_SELF_HOSTED` (the `VITE_DOCUSEAL_SELF_HOSTED` boolean),
 * which encodes CSRF posture and stays the sole signal for the authenticity-token decision.
 */
export const hasSelfHostedDocuseal = (): boolean => !!DOCUSEAL_HOST;

/**
 * The DocuSeal submitter form URL for a slug — `<base>/s/<slug>` on a self-hosted instance, else the
 * SaaS host. The slug is encoded because it's interpolated into the embed's `data-src` HTML attribute
 * (rendered via dangerouslySetInnerHTML), so a stray character can't break out of the attribute.
 */
export const submitterFormUrl = (slug: string): string =>
  `${DOCUSEAL_BASE_URL || 'https://docuseal.com'}/s/${encodeURIComponent(slug)}`;

const docusealEnvironment = {
  DOCUSEAL_BASE_URL,
  DOCUSEAL_HOST,
  DOCUSEAL_SELF_HOSTED,
};

export default docusealEnvironment;
